'use client';

import classNames from 'classnames';
import {
  type ButtonHTMLAttributes,
  type ChangeEventHandler,
  forwardRef,
  type MouseEventHandler,
  type ReactNode,
  useCallback,
  useRef,
} from 'react';

import type { ColorTypeModel, SizeType } from '../../models';

import ButtonSpinner from '../Spinners/ButtonSpinner';

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  label: string;
  colorType?: ColorTypeModel;
  size?: SizeType;
  icon?: {
    position: 'leading' | 'trailing';
    element: ReactNode;
  };
  loading?: boolean;
  type?:
    | ButtonHTMLAttributes<ButtonHTMLAttributes<HTMLButtonElement>>['type']
    | 'file';
  onFileChange?: ChangeEventHandler<HTMLInputElement>;
  fileAccept?: string;
}

const sharedThemes = classNames(
  'flex items-center justify-center font-bold',
  'shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] disabled:opacity-40 [&_svg]:text-gray-400',
  'mobileScreen:h-[52px] mobileScreen:py-[15px] mobileScreen:rounded-[8px] mobileScreen:text-base mobileScreen:font-normal mobileScreen:shadow-none'
);

const sizeThemes = {
  xs: 'gap-[8px] px-[11px] h-[30px] rounded-[4px] text-xs leading-[16px] [&_svg]:w-[16px] [&_svg]:h-[16px] ',
  sm: 'gap-[8px] px-[13px] h-[34px] rounded-[6px] text-sm leading-[18px] [&_svg]:w-[16px] [&_svg]:h-[16px] ',
  base: 'gap-[8px] px-[17px] h-[38px] rounded-[6px] text-sm leading-[18px] [&_svg]:w-[20px] [&_svg]:h-[20px] ',
  lg: 'gap-[12px] px-[17px] h-[42px] rounded-[6px] text-base leading-[22px] [&_svg]:w-[20px] [&_svg]:h-[20px] ',
  xl: 'gap-[12px] px-[25px] h-[50px] rounded-[6px] text-base leading-[22px] [&_svg]:w-[20px] [&_svg]:h-[20px] ',
};

const colorTypeThemes: Record<ColorTypeModel, string> = {
  primary: 'bg-Yellow-400 text-CoolGray-900 enabled:hover:bg-Yellow-450 ',
  secondary:
    'bg-CoolGray-800 text-[#FFF] enabled:hover:bg-CoolGray-850 disabled:opacity-25 [&_svg]:text-white ',
  white:
    'bg-white text-CoolGray-900 ring-[1px] ring-NeutralGray-100 ring-inset enabled:hover:bg-CoolGray-10 ',
  coolGray: 'bg-CoolGray-100 text-CoolGray-900 enabled:hover:bg-CoolGray-200 ',
  gray: 'bg-CoolGray-100 text-CoolGray-900 enabled:hover:bg-CoolGray-200 ',
  yellow: 'bg-Yellow-400 text-CoolGray-900 enabled:hover:bg-Yellow-450 ',
};

const iconThemes = {
  leading: {
    xs: 'pl-[9px] ',
    sm: 'pl-[11px] ',
    base: 'pl-[15px] ',
    lg: 'pl-[15px] ',
    xl: 'pl-[23px] ',
  },
  trailing: {
    xs: 'pr-[9px] ',
    sm: 'pr-[11px] ',
    base: 'pr-[15px] ',
    lg: 'pr-[15px] ',
    xl: 'pr-[23px] ',
  },
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    label,
    colorType = 'white',
    size = 'base',
    loading = false,
    icon,
    disabled,
    type = 'button',
    className,
    onClick,
    onFileChange,
    fileAccept,
    ...props
  },
  ref
) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const spinner = <ButtonSpinner size={size} colorType={colorType} />;
  const isTrailingPosition = icon?.position === 'trailing';
  const Icon = loading ? spinner : icon?.element;
  const isDisabled = disabled || loading;

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    _event => {
      if (type === 'file' && fileInputRef.current) {
        fileInputRef.current.click();
        fileInputRef.current.value = '';
        return;
      }
    },
    []
  );

  return (
    <button
      {...props}
      ref={ref}
      className={classNames(
        className ? className : '',
        sharedThemes,
        colorTypeThemes[colorType],
        sizeThemes[size],
        { 'cursor-not-allowed': isDisabled },
        { 'flex-row-reverse': isTrailingPosition },
        icon ? iconThemes[icon.position][size] : '',
        'outline-none'
      )}
      style={{ fontFeatureSettings: "'tnum'" }}
      disabled={isDisabled}
      type={type === 'file' ? 'button' : type}
      onClick={onClick ?? handleClick}
    >
      {Icon}
      {label}
      {type === 'file' && (
        <input
          onChange={onFileChange}
          multiple={false}
          ref={fileInputRef}
          type="file"
          hidden
          accept={fileAccept}
        />
      )}
    </button>
  );
});

export default Button;
